'use client';

import { useContext, useEffect, useRef } from 'react';
import classnames from 'classnames';
import { Reveal } from '@stadionhq-v3/base-ui';
import {
  StreamProvider,
  useStreamPlaybackAccess,
} from '@stadionhq-v3/stream-ui';
import { StreamPackagePlayerOverlay } from '@components/organisms/stream-package-player-overlay';
import { StreamSessionContext } from '@providers/streamSessionProvider';
import { forceSignOut } from '@utils/auth/forceSignOut';
import { LoadingTemplate } from '@components/templates/loading-template';
import styles from './streamVideoAndOverlayWrapper.module.scss';
import { StreamVideoAndOverlayWrapperProps } from './streamVideoAndOverlayWrapper.types';

export const StreamVideoAndOverlayWrapper = ({
  videoId,
  dictionary,
  packagePageLinkPath,
  videoComponent,
}: StreamVideoAndOverlayWrapperProps) => {
  const context = useContext(StreamSessionContext); // Access the stream session context

  const forceSignoutRef = useRef(() => forceSignOut(context?.ssoJwt as string));

  const { playbackAccess, checkedPlaybackAccess, cloudmatrixEntry, loading } =
    useStreamPlaybackAccess(
      videoId as string,
      context?.ssoJwt as string,
      forceSignoutRef.current,
      true
    );

  // Update the ref when ssoJwt is set
  useEffect(() => {
    if (context?.ssoJwt) {
      forceSignoutRef.current = () => forceSignOut(context.ssoJwt as string);
    }
  }, [context?.ssoJwt]);

  // Ensure the component is used within a StreamSessionProvider
  if (!context) {
    throw new Error(
      'StreamSessionContext must be used within a StreamSessionProvider'
    );
  }

  const { account, ssoJwt } = context;

  const isLive = cloudmatrixEntry?.mediaData?.mediaType === 'Live';

  return (
    <>
      {loading && (
        <div className={styles.loader}>
          <LoadingTemplate label={dictionary.loading} />
        </div>
      )}

      {!loading && (
        <StreamProvider token={ssoJwt ?? ''}>
          {playbackAccess && videoComponent}

          {/* Overlay for users without an account */}
          {!account && !playbackAccess && checkedPlaybackAccess && (
            <Reveal fadeIn delay={100} distance="0px">
              <StreamPackagePlayerOverlay
                classNames={[classnames(styles.playerOverlay)]}
                title={
                  isLive
                    ? dictionary.liveSubscribersOnlyTitle
                    : dictionary.subscribersOnlyTitle
                }
                message={
                  isLive
                    ? dictionary.liveSubscribersOnlyDescription
                    : dictionary.subscribersOnlyDescription
                }
                signInCta={{
                  label: dictionary.logInCTA,
                }}
                registerCta={{ label: dictionary.registerCTA }}
              />
            </Reveal>
          )}

          {/* Overlay for users who don't have access to the video */}
          {account && !playbackAccess && checkedPlaybackAccess && (
            <Reveal fadeIn delay={100} distance="0px">
              <StreamPackagePlayerOverlay
                classNames={[classnames(styles.playerOverlay)]}
                title={
                  isLive
                    ? dictionary.purchaseSubscriptionTitle
                    : dictionary.purchaseSubscriptionTitle
                }
                message={
                  isLive
                    ? dictionary.livePurchaseSubscriptionDescription
                    : dictionary.purchaseSubscriptionDescription
                }
                cta={
                  packagePageLinkPath
                    ? {
                        label: isLive
                          ? dictionary.purchaseSubscriptionCTALabel
                          : dictionary.purchaseSubscriptionCTALabel,
                        url: packagePageLinkPath,
                      }
                    : undefined
                }
              />
            </Reveal>
          )}
        </StreamProvider>
      )}
    </>
  );
};
